import React, { useState, useEffect } from "react";
import Container from "@mui/material/Container";
import "react-confirm-alert/src/react-confirm-alert.css";
import {
  Box,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import { CalendarToday, Delete as DeleteIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import CleaningServicesOutlinedIcon from "@mui/icons-material/CleaningServicesOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import * as moment from "moment";

// Add imports for the DatePicker and Adapter
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

const Patient = () => {
  const token = localStorage.getItem("Thyra_Token");
  const auth = `Bearer ${token}`;
  const headers = {
    Authorization: auth,
    "Content-Type": "application/json",
  };
  const navigate = useNavigate();

  const permissions = localStorage.getItem("permissionStored");
  const parsedPermissions = JSON.parse(permissions);
  console.log("add_patient permission:", parsedPermissions.add_patient);
  const addPermission = parsedPermissions.add_patient;


  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [filter, setFilter] = useState({
    fullName: "",
    dob: "",
    phoneNo: "",
  });
  const authR = { delete_patient: true };

  useEffect(() => {
    fetchPatients();
  }, [refresh]);

  const fetchPatients = () => {
    setIsLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/Patient/getPatientList`, {
        headers,
      })
      .then((response) => {
        const formattedData = response.map((patient) => ({
          ...patient,
          DOB: patient.dob ? moment(patient.dob).format("MM/DD/YYYY") : "",
          fullName: `${patient.lastName || ""} ${
            patient.firstName || ""
          }`.trim(),
        }));
        setRows(formattedData);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setIsLoading(false);
      });
  };

  const openPatientRegistration = (event, id) => {
    navigate("/PatientRegistration", { state: { patientId: id } });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };


  const onFilterSearch = () => {
    setIsLoading(true);
    const queryParams = new URLSearchParams();
    if (filter.fullName) {
      const [firstName, ...lastNameParts] = filter.fullName.split(" ");
      const lastName = lastNameParts.join(" ");
      if (firstName) queryParams.append("firstName", firstName);
      if (lastName) queryParams.append("lastName", lastName);
    }
    if (filter.dob) queryParams.append("dob", filter.dob);
    if (filter.phoneNo) queryParams.append("phoneNo", filter.phoneNo);

    axios
      .get(
        `${
          process.env.REACT_APP_API_URL
        }/Patient/SearchPatient?${queryParams.toString()}`,
        { headers }
      )
      .then((response) => {
        const formattedData = response.map((patient) => ({
          ...patient,
          DOB: patient.dob ? moment(patient.dob).format("MM/DD/YYYY") : "",
          fullName: `${patient.firstName || ""} ${
            patient.lastName || ""
          }`.trim(),
        }));
        setRows(formattedData);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error searching patients:", error);
        setIsLoading(false);
      });
  };

  const onFilterClear = () => {
    setFilter({
      fullName: "",
      dob: "",
      phoneNo: "",
    });
    fetchPatients();
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      cellClassName: "gridCell",
      sortable: true,
      headerClassName: "super-app-theme--header",
      flex: 0.05,
    },
    {
      field: "fullName",
      headerName: "Full Name",
      cellClassName: "gridCell",
      sortable: true,
      headerClassName: "super-app-theme--header",
      flex: 0.3,
      renderCell: (cellValues) => (
        <Button
          sx={{
            color: "#000000",
            textTransform: "none",
            justifyContent: "flex-start",
            fontWeight: "bold",
            fontSize: "0.95rem",
            "&:hover": {
              color: "#195C5F",
              textDecoration: "underline",
              transition: "color 0.2s ease-in-out",
            },
          }}
          onClick={() => {
            openPatientRegistration(null, cellValues.row.id);
          }}
        >
          {cellValues.row.fullName}
        </Button>
      ),
    },
    {
      field: "DOB",
      headerName: "DOB",
      cellClassName: "gridCell",
      sortable: true,
      headerClassName: "super-app-theme--header",
      flex: 0.1,
    },
    {
      field: "state",
      headerName: "State",
      cellClassName: "gridCell",
      sortable: true,
      headerClassName: "super-app-theme--header",
      flex: 0.1,
    },
    {
      field: "diagnosis",
      headerName: "Diagnosis",
      cellClassName: "gridCell",
      sortable: true,
      headerClassName: "super-app-theme--header",
      flex: 0.15,
    },
    ...(authR.delete_patient
      ? [
          {
            field: "delete",
            headerClassName: "super-app-theme--header",
            headerName: "Action",
            sortable: false,
            width: 90,
            renderCell: (cellValues) => (
              <DeleteIcon
                style={{
                  marginRight: "5px",
                  marginTop: "5px",
                  color: "red",
                  cursor: "pointer",
                }}
                onClick={() => {
                  Swal.fire({
                    title: "Are you sure?",
                    text: "You won't be able to revert this!",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Yes, delete it!",
                    cancelButtonText: "No, cancel!",
                    reverseButtons: true,
                  }).then((result) => {
                    if (result.isConfirmed) {
                      Swal.fire({
                        title: "Deleted!",
                        text: "Your file has been deleted.",
                        icon: "success",
                        timer: 1000,
                      });

                      const postData = {
                        id: cellValues.row.id,
                        inactive: true,
                      };

                      axios
                        .post(
                          `${process.env.REACT_APP_API_URL}/Patient/addPatient`,
                          postData,
                          { headers }
                        )
                        .then(() => {
                          setRefresh(!refresh);
                        })
                        .catch((error) => {
                          console.error("There was an error!", error);
                        });
                    } else if (result.dismiss === Swal.DismissReason.cancel) {
                      Swal.fire({
                        title: "Cancelled",
                        text: "Your file is safe :)",
                        icon: "error",
                        timer: 1000,
                      });
                    }
                  });
                }}
              />
            ),
          },
        ]
      : []),
  ];

  const gridRowStyle = {
    boxShadow: 2,
    border: 2,
    borderRadius: 2,
    background: "dark",
    fontSize: "13.4px",
    color: "dark",
    borderColor: "#326C1D",
    "& .MuiDataGrid-cell:hover": {
      color: "rgba(5, 152, 236, 0.637)",
      fontSize: "14.6px",
    },
    "& .super-app-theme--header": {
      backgroundColor: "#2B7B7E",
      borderRadius: 0.1,
      color: "white",
      fontSize: "16px",
    },
  };

  return (
    <div className="page-content">
      {/* Wrap the component with LocalizationProvider */}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Container>
          {/* <Box sx={{ mb: 3 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", color: "#2B7B7E" }}
            >
              Patient
            </Typography>
          </Box> */}
          {/* Add New Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
            }}
          >
            {
              addPermission  ?
            (
            <Button
              variant="contained"
              color="primary"
              sx={{
                backgroundColor: "#2B7B7E",
                borderRadius: "80px",
                textTransform: "uppercase",
                fontWeight: "bold",
              }}
              onClick={(event) => openPatientRegistration(event, null)}
            >
              + Add New
            </Button>
            ):(null)
                 }
          </Box>

          {/* Filter Form */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              p: 4,
              border: "1px solid #2B7B7E",
              borderRadius: 1,
              boxShadow: "0 0 0 1px #2B7B7E",
            }}
          >
            <TextField
              label="Search Full Name or First Name"
              placeholder="Please enter full name"
              variant="outlined"
              name="fullName"
              value={filter.fullName}
              onChange={handleFilterChange}
              sx={{ flex: 2 }}
            />
            {/* Replace TextField with DatePicker */}
            <DatePicker
              label="DOB"
              value={filter.dob ? moment(filter.dob, "MM/DD/YYYY") : null}
              onChange={(newValue) => {
                const formattedDate = newValue
                  ? moment(newValue).format("MM/DD/YYYY")
                  : "";
                setFilter((prev) => ({ ...prev, dob: formattedDate }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  sx={{ flex: 1 }}
                  placeholder="mm/dd/yyyy"
                />
              )}
              components={{
                OpenPickerIcon: CalendarToday,
              }}
            />
            <TextField
              label="Phone No"
              placeholder="Enter Phone No"
              variant="outlined"
              name="phoneNo"
              value={filter.phoneNo}
              onChange={handleFilterChange}
              sx={{ flex: 1 }}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                className="Button"
                onClick={onFilterClear}
                variant="contained"
                style={{
                  marginTop: "10px",
                  marginLeft: "auto",
                  float: "right",
                  borderRadius: "80px",
                  width: "90px",
                  height: "35px",
                  backgroundColor: "#2B7B7E",
                  color: "white",
                  fontSize: "14px",
                }}
              >
                <CleaningServicesOutlinedIcon
                  fontSize="medium"
                  style={{ color: "white", paddingRight: "5px" }}
                />
                Clear
              </Button>
              <Button
                className="Button"
                onClick={onFilterSearch}
                variant="contained"
                style={{
                  marginTop: "10px",
                  float: "right",
                  borderRadius: "80px",
                  marginLeft: "10px",
                  width: "90px",
                  height: "35px",
                  backgroundColor: "#2B7B7E",
                  color: "white",
                  fontSize: "14px",
                }}
              >
                <SearchOutlinedIcon
                  fontSize="medium"
                  style={{ color: "white", paddingBottom: "1px" }}
                />
                Search
              </Button>
            </Box>
          </Box>

          {/* Data Grid */}
          <Box sx={{ height: 600, width: "100%" }}>
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 5,
                    },
                  },
                }}
                pageSizeOptions={[5]}
                disableSelectionOnClick
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    bgcolor: "#F5F7F9",
                    color: "#2B7B7E",
                    fontWeight: "bold",
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #E0E0E0",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    bgcolor: "#F5F7F9",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    backgroundColor: "#ffffff",
                  },
                }}
              />
            )}
          </Box>
        </Container>
      </LocalizationProvider>
    </div>
  );
};

export default Patient;
